import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Resizable } from 'react-resizable';
import { Ps, PsTreeItem } from '../../../../../api/model';
import useContextMenu from '../../../../../hooks/useContextMenu';
import * as sessionStorageUtils from '../../../../../utils/sessionStorage';
import WaitComponent from '../../../../atoms/WaitComponent';
import OkModal from '../../../../modules/OkModal';
import { PsDetailsMode } from '../constants/types';
import PsAddDialog from './PsAddDialog';
import PsDiffDialog from './PsDiffDialog';
import PsTreeNode from './PsTreeNode';
import { PsTreeResizeHandle } from './PsTreeResizeHandle';
import { SortableTree } from './dnd-kit/SortableTree';
import { TreeItem, TreeItems } from './dnd-kit/types';

/** PsTree と PsTreeGetter、PsDiffTreeGetter で共通の props (PsDiffTreeGetter には渡さない、からは渡されない props もあるけど)。 */
export interface PsTreePsTreeGetterCommonProps {
  /** PS 閲覧・追加・編集・比較の内、どのモードかを示す。 */
  mode: PsDetailsMode;

  /** URL で指定された PS バージョン ID か、ログイン後 2 回目以降の表示の場合に前回選択されていた項目の PS バージョン ID。ログイン後、初回の表示の場合は未選択状態で undefined となる。 */
  selectedPsVersionId?: number | undefined;

  /** selectedPsVersionId が指す PsTreeItem。 */
  selectedPsTreeItem?: PsTreeItem | undefined;

  /** 操作の対象となる PS。 */
  targetPs?: Ps | undefined;

  // /** 改廃案などを選択状態にするために必要。PsDetails で取得した Ps。PS ツリーには承認済み最新版 PS の PsTreeItem しかなく、URL の PS バージョン ID だけでは PS ID が分からないため、必要となる。 */
  // targetPsId?: number | undefined;

  /** 選択されたタイトル PS 直下のコンテンツ PSs の PsTreeItems。 */
  selectedChildContentsPsTreeItems?: PsTreeItem[] | undefined;

  // /** 選択されたタイトル PS 直下のコンテンツ PS の PS IDs。 */
  // selectedChildContentsPsIds?: number[] | undefined;

  // /** 選択されたタイトル PS 直下のコンテンツ PS のバージョン IDs。 */
  // selectedChildContentsPsVersionIds?: number[] | undefined;

  /** PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時) に呼ばれる関数。 */
  setSelectedPsVersionId?: (selectedPsVersionId: number) => void | undefined;

  /** PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時) に呼ばれる関数。 */
  setSelectedPsTreeItem?: (selectedPsTreeItem: PsTreeItem) => void | undefined;

  /** PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時か、prop の selectedPsVersionId が psTreeItem.ps_version_id と等しい時) に呼ばれる関数。 */
  setSelectedChildContentsPsTreeItems?: (selectedChildContentsPsTreeItems: PsTreeItem[]) => void | undefined;

  // /** PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時か、prop の selectedPsVersionId が psTreeItem.ps_version_id と等しい時) に呼ばれる関数。 */
  // setSelectedChildContentsPsIdVersionIds?: (selectedChildContentsPsIdVersionIds: number[][]) => void | undefined;

  // /** PS 閲覧画面で PS ツリーの項目を選択した時 (クリック時か、prop の selectedPsVersionId が psTreeItem.ps_version_id と等しい時) に呼ばれる関数。 */
  // setSelectedChildContentsPsVersionIds?: (selectedChildContentsPsVersionIds: number[]) => void | undefined;

  /** ドラッグ & ドロップでドロップ時に呼ばれる関数。引数に変更されたルートの PsTreeItem が渡される。 */
  setModifiedRootPsTreeItem?: (modifiedRootPsTreeItem: PsTreeItem) => void | undefined;
}

/** PsTree 固有の props。 */
interface Props extends PsTreePsTreeGetterCommonProps {
  /** PS ツリーのルートとなる PsTreeItem。 */
  rootPsTreeItem: PsTreeItem | undefined;

  /** 差分が存在するPSのPSIDリスト(PSツリー比較用) */
  diffPsIds?: number[];

  /** PsTreeGetter から渡され、PsTreeNode に渡す関数。PostPsTreeSearchParams (/pstrees/search のパラメーター) の disp_root_ps_id に渡す値をセットする。 */
  setPsIdToFetchDescendants?: (psIdToFetchDescendants: number | undefined) => void | undefined;
}

/** PS ツリー。 */
function PsTree({
  mode,
  rootPsTreeItem,
  diffPsIds,
  selectedPsVersionId,
  selectedPsTreeItem,
  targetPs,
  // targetPsId,
  selectedChildContentsPsTreeItems,
  // selectedChildContentsPsIds,
  // selectedChildContentsPsVersionIds,
  setSelectedPsVersionId,
  setSelectedPsTreeItem,
  setSelectedChildContentsPsTreeItems,
  // setSelectedChildContentsPsIdVersionIds,
  // setSelectedChildContentsPsVersionIds,
  // targetPs,
  setModifiedRootPsTreeItem,
  setPsIdToFetchDescendants,
}: Props) {
  // i18n 用。
  const {
    t,
    // i18n,
  } = useTranslation();

  // /** 追加・編集状態。 */
  // const isAddingOrEditing = ((mode === 'Add') || (mode === 'Edit'));

  /** PS ツリーを編集 (ドラッグ & ドロップ) するか否か。 */
  const doEditPsTree = useMemo(
    () =>
      // ルート PS (L0 PS) および章 PS (L1 PS) の編集時のみ可能。
      mode === 'Edit' &&
      // PS 編集画面では PS が取得された後に targetPs が undefined → Ps になる。
      Number(targetPs?.type) <= 1,
    // (Number(selectedPsTreeItem?.type) <= 1)
    [mode, targetPs?.type]
  );

  // console.log('targetPs in PsTree', targetPs);
  // console.log('doEditPsTree', doEditPsTree);

  // ログイン後、2 回目以降の表示の場合、前回選択された言語を初期値とする。
  /** 表示言語の state。 */
  const [languageId, setLanguageId] = useState<number>(sessionStorageUtils.get('PsTree.LanguageId', 0));

  // console.log('languageId: ', languageId);

  /** resize 用。 */
  const [psTreeWidth, setPsTreeWidth] = useState<number>(200);

  /** PS 種別選択ダイアログ (PS 追加用) 表示・非表示 state。 */
  const [isPsAddDialogOpen, setIsPsAddDialogOpen] = useState<boolean>(false);

  /** PS 比較ダイアログ (PS 比較画面へ遷移する用) 表示・非表示 state。 */
  const [isPsDiffDialogOpen, setIsPsDiffDialogOpen] = useState<boolean>(false);

  /** 右クリックで選択された PS ツリーの項目の PsTreeItem。 */
  const [rightClickedPsTreeItem, setRightClickedPsTreeItem] = useState<PsTreeItem | undefined>(undefined);

  // /** 右クリックで選択された PS ツリーの項目の PS バージョン ID。 */
  // const [rightClickedPsVersionId, setRightClickedPsVersionId] = useState<number | undefined>(undefined);

  // /** 右クリックで選択された PS ツリーの項目の PS 種別。 */
  // const [rightClickedPsType, setRightClickedPsType] = useState<number | undefined>(undefined);

  // コンテキスト メニュー用のカスタム フック。
  // 参照: https://blog.logrocket.com/creating-react-context-menu/
  const { clicked, setClicked, points, setPoints } = useContextMenu();
  // PsTree に 1 つのコンテキスト メニュー。PsTreeItem にそれぞれ 1 つではない。
  // 共通コンポーネントにはちょっとしにくいので PsTree にコンテキスト メニューを作成する。
  // https://mui.com/material-ui/react-menu/#context-menu を使用するという手もあるが、ロジック的にはあまり変わらなそうで、コンテキスト メニューのためだけに Material UI を入れるのもちょっとオーバーか。もし Material UI の細かい機能が (画面端では表示が変わる等) 欲しくなったら導入することにする。

  /** 指定された PS まで展開するための配列。 */
  const psIdsToOpenChildren = useMemo(
    () =>
      // eslint-disable-next-line no-nested-ternary
      rootPsTreeItem
        ? diffPsIds !== undefined && diffPsIds.length > 0
          ? diffPsIds.flatMap((psId) => traversePsTreeItemsToOpenChildren(rootPsTreeItem, psId))
          : traversePsTreeItemsToOpenChildren(rootPsTreeItem, selectedPsTreeItem?.ps_id)
        : [],
    [rootPsTreeItem, selectedPsTreeItem?.ps_id, diffPsIds]
  );

  // ドラッグ & ドロップ。
  // https://github.com/clauderic/dnd-kit/tree/master/stories/3%20-%20Examples/Tree
  // を参考に実装する。

  const [sortableTreeItems, setSortableTreeItems] = useState<TreeItems>([]);

  // 比較用に以前の値を保存。
  const [previousRootPsTreeItem, setPreviousRootPsTreeItem] = useState<PsTreeItem | undefined>(undefined);

  // console.log('rootPsTreeItem:', rootPsTreeItem);
  // console.log('previousRootPsTreeItem:', previousRootPsTreeItem);

  // console.log('sortableTreeItems:', sortableTreeItems);

  useEffect(() => {
    if (targetPs) {
      // targetPs に依存するので。

      if (
        // 前回と異なっていたら。
        !_.isEqual(previousRootPsTreeItem, rootPsTreeItem) &&
        // PS ツリーを編集する機能がオンの場合。
        doEditPsTree
      ) {
        if (rootPsTreeItem) {
          // データ取得時にドラッグ & ドロップ用データにする。
          setSortableTreeItems([
            traversePsTreeItemsToCreateData(rootPsTreeItem, targetPs, psIdsToOpenChildren, undefined),
          ]);
        } else {
          // 空にする。
          setSortableTreeItems([]);
        }
      }

      if (!_.isEqual(previousRootPsTreeItem, rootPsTreeItem)) {
        // 以前の値と異なっていたら保存。
        setPreviousRootPsTreeItem(rootPsTreeItem);
      }
    }
  }, [doEditPsTree, previousRootPsTreeItem, psIdsToOpenChildren, rootPsTreeItem, targetPs]);

  // PsTreeItem に対応するコンポーネントと、その子供たち (child_tree_items) を再帰で対応するコンポーネントにして children として置く。
  const traversePsTreeItemsToRenderPsTree = (psTreeItem: PsTreeItem, level: number) => (
    <PsTreeNode
      key={psTreeItem.ps_id}
      mode={mode}
      psTreeItem={psTreeItem}
      languageId={languageId}
      doOpenChildren={
        // 章 PS までは展開しておく (ルート PS の children が見えるようにする)。
        psTreeItem.type === 0 ||
        // 指定された PS まで展開する。
        psIdsToOpenChildren.includes(psTreeItem.ps_id as number)
      }
      level={level}
      selectedPsVersionId={selectedPsVersionId}
      selectedPsTreeItem={selectedPsTreeItem}
      // selectedPsId={targetPs?.ps_id}
      targetPsId={targetPs?.ps_id}
      selectedChildContentsPsTreeItems={selectedChildContentsPsTreeItems}
      // selectedChildContentsPsIds={selectedChildContentsPsIds}
      // selectedChildContentsPsVersionIds={selectedChildContentsPsVersionIds}
      setSelectedPsVersionId={setSelectedPsVersionId}
      setSelectedPsTreeItem={setSelectedPsTreeItem}
      setSelectedChildContentsPsTreeItems={setSelectedChildContentsPsTreeItems}
      // setSelectedChildContentsPsIdVersionIds={setSelectedChildContentsPsIdVersionIds}
      // setSelectedChildContentsPsVersionIds={setSelectedChildContentsPsVersionIds}
      setClickedTrue={() => {
        setClicked(true);
      }}
      setPoints={(newX: number, newY: number) => {
        setPoints({
          x: newX,
          y: newY,
        });
      }}
      setRightClickedPsTreeItem={(newRightClickedPsTreeItem: PsTreeItem) => {
        setRightClickedPsTreeItem(newRightClickedPsTreeItem);
      }}
      // setRightClickedPsVersionId={
      //   (newRightClickedPsVersionId: number) => {
      //     setRightClickedPsVersionId(newRightClickedPsVersionId);
      //   }
      // }
      // setRightClickedPsType={
      //   (newRightClickedPsType: number) => {
      //     setRightClickedPsType(newRightClickedPsType);
      //   }
      // }
      // targetPs={targetPs}
      setPsIdToFetchDescendants={setPsIdToFetchDescendants}
      targetPsType={targetPs?.type}
    >
      {Array.isArray(psTreeItem.child_tree_items) &&
        psTreeItem.child_tree_items.map(
          // 再帰。
          (childPsTreeItem: PsTreeItem) => traversePsTreeItemsToRenderPsTree(childPsTreeItem, level + 1)
        )}
    </PsTreeNode>
  );

  // クリップボードに参照IDをコピー
  const [displayOkModal, setDisplayOkModal] = useState(false);
  const copyToClipboard = async () => {
    // console.log(rightClickedPsTreeItem?.titles);
    await global.navigator.clipboard.writeText(
      rightClickedPsTreeItem !== undefined ? `#${Number(rightClickedPsTreeItem.ps_id)}` : ''
    );
    setDisplayOkModal(true);
  };

  return (
    <>
      <Resizable
        width={psTreeWidth}
        height={0}
        className="box"
        onResize={(event, { node, size, handle }) => {
          setPsTreeWidth(Number(size.width));
        }}
        handle={<PsTreeResizeHandle />}
        resizeHandles={['e']}
      >
        <>
          <div
            id="product-side-list"
            style={{
              // position: 'absolute',
              // maxHeight: '90vh',
              width: `${psTreeWidth}px`,
              maxWidth: `600px`,
            }}
          >
            <div id="pslfr">
              <div className="productmenu">
                <div className="pm-form2">
                  <label>{t('表示言語')}</label>
                  <span className="dropdown">
                    <select
                      // 表示言語の切替を state で実装。
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        setLanguageId(parseInt(event.target.value, 10));
                        // セッション ストレージに保存。
                        sessionStorageUtils.set('PsTree.LanguageId', parseInt(event.target.value, 10));
                      }}
                      value={languageId}
                    >
                      {[t('日本語'), t('米語'), t('英語')].map((language: string, index: number) => (
                        <option key={language} value={index}>
                          {language}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>

                <div
                  className="tree scroll"
                  style={{
                    maxHeight: '80vh',
                  }}
                >
                  {/* <i className="fa-solid fa-book" /> */}
                  {!rootPsTreeItem && <WaitComponent />}

                  {
                    // ドラッグ & ドロップ機能がオンの場合。
                    doEditPsTree
                      ? sortableTreeItems.length > 0 &&
                        rootPsTreeItem && (
                          // ドラッグ & ドロップ。
                          // https://github.com/clauderic/dnd-kit/tree/master/stories/3%20-%20Examples/Tree
                          // を参考に実装する。
                          // TODO?: defaultItems と onItemsChange じゃなくて、items と setItems を渡すか？
                          <SortableTree
                            defaultItems={sortableTreeItems}
                            languageId={languageId}
                            targetL1PsId={
                              // (Number(selectedPsTreeItem?.type) === 1) ?
                              //   Number(selectedPsTreeItem?.ps_id)
                              Number(targetPs?.type) === 1 ? Number(targetPs?.ps_id) : undefined
                            }
                            onItemsChange={(treeItems: TreeItems) => {
                              // console.log('treeItems:', treeItems);

                              if (treeItems.length > 0) {
                                // 変更されたルートの PsTreeItem を引数として prop で渡されてきた関数に渡す。
                                if (typeof setModifiedRootPsTreeItem === 'function') {
                                  setModifiedRootPsTreeItem(
                                    // 取得したデータと、変更されたデータを比較して、POST 用データにする。
                                    getModifiedPsTreeItem(treeItems[0], rootPsTreeItem, rootPsTreeItem, 0)
                                  );
                                }
                              }
                            }}
                            indicator
                            collapsible
                            indentationWidth={14}
                          />
                        )
                      : // ドラッグ & ドロップしない場合。
                        rootPsTreeItem &&
                        // 取得したデータをそのまま描画。
                        traversePsTreeItemsToRenderPsTree(rootPsTreeItem, 0)
                  }
                </div>
              </div>
            </div>
            {/* ↓ 不要？ */}
            {/* <p id="move-cursor" /> */}

            {/* コンテキスト メニュー。 */}
            {/* 
              PsTree に 1 つのコンテキスト メニュー。PsTreeItem にそれぞれ 1 つではない。
              onContextMenu は PsTreeNode に実装し、表示状態は PsTree で管理する必要がある。
            */}
            {clicked && (
              <ul
                className="popup psTreeContextMenu"
                style={{
                  position: 'fixed',
                  top: `${points.y}px`,
                  left: `${points.x}px`,

                  // display: 'block',
                  // boxSizing: 'border-box',
                  // margin: 0,
                  // listStyle: 'none',

                  // backgroundColor: '#ffffff',

                  // WebkitBoxShadow: '1px 1px 1px 1px rgba(211, 211, 211, 0.5)',
                  // boxShadow: '1px 1px 1px 1px rgba(211, 211, 211, 0.5)',

                  // borderRadius: '4px',

                  // zIndex: 5,

                  // padding: '8px 8px 3px 8px',
                  // marginBottom: '5px',

                  // border-radius: 4px;
                  // z-index: 5;

                  // padding: 8px 8px 3px 8px;
                  // width: 140px;
                  // margin-bottom: 5px;
                }}
              >
                {mode === 'View' && (
                  <>
                    <li
                      className="modal-open"
                      onClick={() => {
                        // PS 新規作成ダイアログを表示する。
                        setIsPsAddDialogOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-circle-plus" />
                      {t('追加')}
                    </li>
                    <li
                      className="modal-open"
                      onClick={() => {
                        // PS 差分指定ダイアログを表示する。
                        setIsPsDiffDialogOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-code-compare" />
                      {t('比較')}
                    </li>
                  </>
                )}
                {(mode === 'Edit' || mode === 'Add') && (
                  <li className="modal-open" onClick={() => copyToClipboard()}>
                    <i className="fa-solid fa-copy" />
                    {t('IDコピー')}
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* https://www.npmjs.com/package/react-resizable */}

          {/* <div className="splitter" /> */}
        </>
      </Resizable>

      {/* PS 種別選択ダイアログ (PS 追加用)。 */}
      {isPsAddDialogOpen && rightClickedPsTreeItem && (
        <PsAddDialog
          isOpen={isPsAddDialogOpen}
          setIsOpen={(newIsOpen: boolean) => {
            setIsPsAddDialogOpen(newIsOpen);
          }}
          selectedPsTreeItem={rightClickedPsTreeItem}
          // selectedPsVersionId={rightClickedPsVersionId as number}
          // selectedPsType={rightClickedPsType as number}
        />
      )}

      {/* PS 差分指定ダイアログ (PS 比較画面へ遷移する用)。 */}
      {isPsDiffDialogOpen && (
        // state のリセットなどが少々面倒なので、丸ごと再描画とする。
        // TODO?: ダイアログを表示する度に getPs し、取得完了まで待つ必要があるが、構わないか。
        <PsDiffDialog
          isOpen={isPsDiffDialogOpen}
          setIsOpen={(newIsOpen: boolean) => {
            setIsPsDiffDialogOpen(newIsOpen);
          }}
          selectedPsVersionId={rightClickedPsTreeItem?.ps_version_id as number}
          selectedPsType={rightClickedPsTreeItem?.type as number}
        />
      )}

      {/* クリップボードコピーの確認メッセージ */}
      {displayOkModal && (
        <OkModal onClickOk={() => setDisplayOkModal(false)}>
          <div>{t('IDをコピーしました。')}</div>
        </OkModal>
      )}
    </>
  );
}

PsTree.defaultProps = {
  diffPsIds: [],
  selectedPsVersionId: undefined,
  selectedPsTreeItem: undefined,
  selectedChildContentsPsTreeItems: undefined,
  // selectedChildContentsPsIds: undefined,
  // selectedChildContentsPsVersionIds: undefined,
  setSelectedPsVersionId: undefined,
  setSelectedPsTreeItem: undefined,
  targetPs: undefined,
  // targetPsId: undefined,
  setSelectedChildContentsPsTreeItems: undefined,
  // setSelectedChildContentsPsIdVersionIds: undefined,
  // setSelectedChildContentsPsVersionIds: undefined,
  setModifiedRootPsTreeItem: undefined,
  setPsIdToFetchDescendants: undefined,
};

export default PsTree;

/** 取得したデータから SortableTree 用のデータにする。 */
const traversePsTreeItemsToCreateData = (
  psTreeItem: PsTreeItem,
  targetPs: PsTreeItem | undefined,
  psIdsToOpenChildren: number[],
  l1PsId: number | undefined
): TreeItem => {
  const treeItem: TreeItem = {
    id: psTreeItem.ps_id as number,
    children: [],
    collapsed: !(
      // 章 PS までは展開しておく (ルート PS の children が見えるようにする)。
      (
        psTreeItem.type === 0 ||
        // 指定された PS まで展開する。
        psIdsToOpenChildren.includes(psTreeItem.ps_id as number)
      )
    ),
    psTreeItem,
    // 編集対象か否か。PS ID で判断。
    isTarget: psTreeItem.ps_id === targetPs?.ps_id,
    l1PsId,
  };

  if (Array.isArray(psTreeItem.child_tree_items)) {
    psTreeItem.child_tree_items.forEach((childPsTreeItem: PsTreeItem) => {
      // 再帰。
      treeItem.children.push(
        traversePsTreeItemsToCreateData(
          childPsTreeItem,
          targetPs,
          psIdsToOpenChildren,
          // 親が L1 PS なら親の PS ID、そうでなければ親から引き継いだものを渡していく。
          Number(psTreeItem.type) === 1 ? psTreeItem.ps_id : l1PsId
        )
      );
    });
  }

  return treeItem;
};

/** PS ID で指定された PS まで展開するための関数。子供たちを展開する必要のある PS の PS ID の配列を返す。 */
const traversePsTreeItemsToOpenChildren = (psTreeItem: PsTreeItem, psId: number | undefined): number[] => {
  if (typeof psId !== 'number') {
    return [];
  }

  if (psId === psTreeItem.ps_id) {
    // 自身が指定された PS である。-1 を含んだ配列を返す。
    return [-1];
  }

  if (Array.isArray(psTreeItem.child_tree_items)) {
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < psTreeItem.child_tree_items.length; index++) {
      // 再帰。
      const returnedArray = traversePsTreeItemsToOpenChildren(psTreeItem.child_tree_items[index], psId);

      if (returnedArray.length > 0) {
        // 配列の長さが 1 以上 → 指定された PS が子孫のどこかにいる。

        if (returnedArray.includes(-1)) {
          // 自分の直下の子供が指定された PS だった。-1 を削除する。
          returnedArray.pop();
        }

        // 子孫のどこかに指定された PS がいるので、自分の直接の子供たちを展開する必要があるため、配列に自身の PS ID を追加。
        returnedArray.unshift(psTreeItem.ps_id as number);

        return returnedArray;
      }
    }
  }

  return [];
};

/** 引数の PS ID に合致する PsTreeItem を返す関数。 */
export const getPsTreeItem = (psTreeItem: PsTreeItem, psId: number): PsTreeItem | undefined => {
  if (psTreeItem.ps_id === psId) {
    return psTreeItem;
  }

  if (Array.isArray(psTreeItem.child_tree_items)) {
    for (let index = 0; index < psTreeItem.child_tree_items.length; index++) {
      const childPsTreeItem: PsTreeItem = psTreeItem.child_tree_items[index];

      // 再帰。
      const foundPsTreeItem = getPsTreeItem(childPsTreeItem, psId);

      if (foundPsTreeItem) {
        return foundPsTreeItem;
      }
    }
  }

  return undefined;
};

/** 取得したデータとドラッグ & ドロップで変更されたデータを比較して、最初の呼び出し元に、変更されたルート PsTreeItem を返す関数。 */
const getModifiedPsTreeItem = (
  // 変更後のデータ。
  treeItem: TreeItem,
  // 変更後のデータと PS バージョン ID が合致する PsTreeItem。
  psTreeItem: PsTreeItem,
  // 検索用にルートの PsTreeItem を渡す。
  rootPsTreeItem: PsTreeItem,
  // 親から渡される modify_type。
  modifyType: number
): PsTreeItem => {
  const modifiedPsTreeItem: PsTreeItem = {
    ...(treeItem.psTreeItem ? treeItem.psTreeItem : {}),
    // 親から渡される modify_type を設定。
    modify_type: modifyType,
    // リセットして、変更後のデータを基に追加していく。
    child_tree_items: [],
  };

  /* eslint no-plusplus: ["error", { "allowForLoopAfterthoughts": true }] */
  for (let index = 0; index < treeItem.children.length; index++) {
    // ドラッグ & ドロップで変更された子供を取得。
    const childTreeItem: TreeItem = treeItem.children[index];
    const modifiedChildPsTreeItem: PsTreeItem | undefined = childTreeItem.psTreeItem;

    // 子供がどのように変更されたかを示す、ツリー編集種別。
    let childModifyType = 0;

    if (modifiedChildPsTreeItem) {
      if (Array.isArray(psTreeItem.child_tree_items) && psTreeItem.child_tree_items.length > index) {
        // 元の子供と比較可能の場合。
        const originalChildPsTreeItem: PsTreeItem = psTreeItem.child_tree_items[index];

        // ツリー上で PS をドラッグ & ドロップした時、移動した PS に modify_type = 2 (自身移動) を設定。
        // その結果として移動元と移動先で元の並び順が変わった PS については modify_type = 4 (兄弟移動) を設定。

        // 移動元と移動先の同じ親を持つ PS について全て編集前の並び順と現在の並び順を比較し、一致しない PS に modify_type = 2 を設定する、一致する場合は modifiy_type = 0 (編集なし) を設定する。

        // 元の並び順と比較。
        if (originalChildPsTreeItem.ps_id !== modifiedChildPsTreeItem.ps_id) {
          childModifyType = modifiedChildPsTreeItem.modify_type === 2 ? 2 : 4; // 直接ドラッグ & ドロップで移動されたPSの場合は2(自身移動),それ以外は4(兄弟移動)を設定
        }
      } else {
        // 比較出来る元の子供がなく、元の子供たちより多い場合。
        childModifyType = modifiedChildPsTreeItem.modify_type === 2 ? 2 : 4; // 直接ドラッグ & ドロップで移動されたPSの場合は2(自身移動),それ以外は4(兄弟移動)を設定
      }

      // 再帰して処理したデータを子供に追加する。
      (modifiedPsTreeItem.child_tree_items as PsTreeItem[]).push(
        getModifiedPsTreeItem(
          childTreeItem,
          // 変更後の PsTreeItem の PS ID を基に、取得したデータの中から一致するものを検索して渡す。
          // ドラッグ & ドロップでは削除されないはずなので、必ず見つかるという前提。
          getPsTreeItem(rootPsTreeItem, modifiedChildPsTreeItem.ps_id as number) as PsTreeItem,
          // ルートはそのまま渡す。
          rootPsTreeItem,
          // 子孫 PS を持つタイトル PS をドラッグ & ドロップした時は、子孫 PS に modify_type = 3 (先祖移動) を設定。
          // 親が 2 か 3 で、子供自身の並び順は変わっていない場合に 3 とする。
          (modifyType === 2 || modifyType === 3) && childModifyType === 0 ? 3 : childModifyType
        )
      );
    }
  }

  return modifiedPsTreeItem;
};
